import { BMInputField, BMPrimitives, BMText } from "@traveloka/district-bloom";
import { ComponentProps, useState } from "react";
import { StyleSheet } from "react-native";
import { BMCountryCodePicker } from "../BMCountryCodePicker";
import BMView from "../View/BMView";
import { PhoneFieldValue } from "../form";
import { getLabel, getPhoneValue, transformPhoneValue } from "./helper";

export type Props = Omit<
  ComponentProps<typeof BMInputField>,
  | "onChange"
  | "onChangeText"
  | "value"
  | "variant"
  | "keyboardType"
  | "onDropdownToggle"
  | "placeholder"
  | "onBlur"
  | "LeadingIcon"
  | "buttonInsetType"
  | "buttonInsetProps"
> & {
  value: PhoneFieldValue | null;
  variant?: "DEFAULT" | "WITH_DASHED";
  isRequired?: boolean;
  prefixOptions?: {
    label?: string;
    placeholder?: string;
  };
  phoneOptions?: {
    label?: string;
    placeholder?: string;
  };
  zIndex?: number;
  onChange?: (value: PhoneFieldValue | null) => void;
  onBlur?: (value: PhoneFieldValue | null) => void;
};

export default function BMPhoneNumberField({
  value,
  error,
  caption,
  phoneOptions,
  prefixOptions,
  variant = "DEFAULT",
  captionAlignment = "left",
  isRequired = false,
  zIndex,
  onChange,
  onBlur,
  ...rest
}: Props) {
  const [selection, setSelection] = useState<{ start: number; end: number }>({
    start: 0,
    end: 0,
  });

  const showTextMessage =
    Boolean(error && typeof error === "string" && error.length > 0) ||
    Boolean(caption && typeof caption === "string" && caption.length > 0);

  function handleChange({
    prefix,
    phone,
  }: {
    prefix?: string | null;
    phone?: string;
  }) {
    const updated: PhoneFieldValue = {
      prefix: prefix ?? value?.prefix ?? "",
      phone: phone ?? value?.phone ?? "",
    };

    onChange?.(updated.prefix || updated.phone ? updated : null);
  }

  function handleBlur() {
    onBlur?.(value);
  }

  function handlePhoneInputChange(text: string) {
    const data = transformPhoneValue(text, value?.phone, selection, variant);
    const { cleaned, cursor } = data;
    handleChange({ phone: cleaned });
    setSelection(cursor);
  }

  return (
    <BMView spacing="xs" style={{ zIndex }}>
      <BMView spacing="lg" justify="between" row style={styles.zIndex10}>
        <BMView flex={0.3}>
          <BMCountryCodePicker
            value={value?.prefix || null}
            label={getLabel(prefixOptions?.label || "Country Code", isRequired)}
            placeholder={prefixOptions?.placeholder || "+62"}
            onChange={(v) => handleChange({ prefix: v })}
            getOptionLabel={(option) =>
              option ? option.value.match(/\+\d+/)?.[0] ?? "" : ""
            }
            error={!!error}
            onBlur={handleBlur}
          />
        </BMView>
        <BMView flex={0.7}>
          <BMInputField
            {...rest}
            error={!!error}
            onBlur={handleBlur}
            selection={selection}
            keyboardType="numeric"
            value={getPhoneValue(value?.phone, variant)}
            onChange={(e) => handlePhoneInputChange(e.nativeEvent.text)}
            onSelectionChange={(e) => setSelection(e.nativeEvent.selection)}
            label={getLabel(phoneOptions?.label || "Mobile Number", isRequired)}
            placeholder={phoneOptions?.placeholder || "Type mobile number here"}
          />
        </BMView>
      </BMView>
      {showTextMessage && (
        <BMText
          style={[styles.helperText, { textAlign: captionAlignment }]}
          variant="uiSmall"
          ink={error ? "BMUiInputInkError" : "BMStyleTextInkSecondary"}
        >
          {error || caption}
        </BMText>
      )}
    </BMView>
  );
}

const styles = StyleSheet.create({
  helperText: {
    marginLeft: BMPrimitives.BMSize.xs,
  },
  zIndex10: {
    zIndex: 10,
  },
});
